import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useEnvironment, AppType } from '../../context/EnvironmentContext';

const Navigation: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout, isAdmin } = useAuth();
  const { currentApp, clearApp } = useEnvironment();
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const handleLogout = () => {
    logout();
    clearApp(); // Also clear selected app on logout
    navigate('/login');
  };

  const handleSwitchApp = () => {
    clearApp(); // Or just navigate, but clearing ensures re-selection logic works
    navigate('/select-app');
  };

  const isActive = (path: string) => {
    return location.pathname === path;
  };

  const navLinkClass = (path: string) => {
    return `px-4 py-2 rounded-md text-sm font-medium min-h-[44px] flex items-center ${isActive(path)
      ? 'bg-[#1a2d5a] text-white'
      : 'text-gray-300 hover:bg-[#1a2d5a] hover:text-white'
      }`;
  };

  const mobileNavLinkClass = (path: string) => {
    return `block px-4 py-3 rounded-md text-base font-medium min-h-[44px] ${isActive(path)
      ? 'bg-[#1a2d5a] text-white'
      : 'text-gray-300 hover:bg-[#1a2d5a] hover:text-white'
      }`;
  };

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const closeMenu = () => {
    setIsMenuOpen(false);
  };

  const getLogo = () => {
    switch (currentApp) {
      case AppType.HAUSARZT:
        return '/hausarzt_logo.svg';
      case AppType.KINDERARZT:
        return '/kinder_logo.svg';
      case AppType.LEGACY:
      default:
        return '/logo_praxis_hayat_seite.webp';
    }
  };



  return (
    <nav className="bg-[#0c1840] shadow-lg">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          <div className="flex items-center gap-3">
            <Link to="/" className="flex items-center gap-3" onClick={closeMenu}>
              <img
                src={getLogo()}
                alt="App Logo"
                className="h-10 w-auto"
              />

            </Link>
          </div>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-4">
            <Link to="/" className={navLinkClass('/')}>
              Dashboard
            </Link>
            <Link to="/zuweisungen" className={navLinkClass('/zuweisungen')}>
              Zuweisungen
            </Link>
            <Link to="/aerzte" className={navLinkClass('/aerzte')}>
              Ärzte
            </Link>
            <Link to="/praxen" className={navLinkClass('/praxen')}>
              Praxen
            </Link>
            {isAdmin && (
              <Link to="/users" className={navLinkClass('/users')}>
                Benutzer
              </Link>
            )}

            {/* User Info and Logout */}
            <div className="flex items-center gap-3 ml-4 pl-4 border-l border-gray-600">
              {/* Switch App Button if user has multiple allowed apps (check not strict here, just allow switch) */}
              <button
                onClick={handleSwitchApp}
                className="text-gray-300 hover:text-white text-sm underline mr-2"
              >
                Bereich wechseln
              </button>

              <div className="text-gray-300 text-sm">
                <div className="font-medium">{user?.username}</div>
                <div className="text-xs text-gray-400">{user?.role}</div>
              </div>
              <button
                onClick={handleLogout}
                className="px-3 py-2 min-h-[44px] bg-red-600 hover:bg-red-700 text-white rounded-md transition-colors text-sm"
                title="Abmelden"
              >
                Abmelden
              </button>
            </div>
          </div>

          {/* Mobile menu button */}
          <div className="md:hidden">
            <button
              onClick={toggleMenu}
              className="inline-flex items-center justify-center p-2 rounded-md text-gray-300 hover:text-white hover:bg-[#1a2d5a] focus:outline-none focus:ring-2 focus:ring-inset focus:ring-white min-h-[44px] min-w-[44px]"
              aria-expanded={isMenuOpen}
              aria-label="Hauptmenü"
            >
              {/* Hamburger icon */}
              {!isMenuOpen ? (
                <svg className="block h-6 w-6" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                </svg>
              ) : (
                <svg className="block h-6 w-6" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile menu */}
      {isMenuOpen && (
        <div className="md:hidden">
          <div className="px-2 pt-2 pb-3 space-y-1 sm:px-3">
            {/* User Info */}
            <div className="px-4 py-3 bg-[#1a2d5a] rounded-md mb-2 flex justify-between items-center">
              <div>
                <div className="text-white font-medium">{user?.username}</div>
                <div className="text-gray-300 text-sm">{user?.role}</div>
              </div>
              <button
                onClick={() => { closeMenu(); handleSwitchApp(); }}
                className="text-gray-300 hover:text-white text-xs underline"
              >
                Wechseln
              </button>
            </div>

            <Link to="/" className={mobileNavLinkClass('/')} onClick={closeMenu}>
              Dashboard
            </Link>
            <Link to="/zuweisungen" className={mobileNavLinkClass('/zuweisungen')} onClick={closeMenu}>
              Zuweisungen
            </Link>
            <Link to="/aerzte" className={mobileNavLinkClass('/aerzte')} onClick={closeMenu}>
              Ärzte
            </Link>
            <Link to="/praxen" className={mobileNavLinkClass('/praxen')} onClick={closeMenu}>
              Praxen
            </Link>
            {isAdmin && (
              <Link to="/users" className={mobileNavLinkClass('/users')} onClick={closeMenu}>
                Benutzer
              </Link>
            )}

            {/* Logout Button */}
            <button
              onClick={() => {
                closeMenu();
                handleLogout();
              }}
              className="w-full text-left px-4 py-3 min-h-[44px] bg-red-600 hover:bg-red-700 text-white rounded-md transition-colors"
            >
              Abmelden
            </button>
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navigation;
